import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  BeakerIcon,
  CameraIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  HeartIcon,
  CubeIcon,
  ShieldCheckIcon,
  PresentationChartBarIcon,
  QueueListIcon,
  UsersIcon,
  UserIcon,
  BanknotesIcon,
  CogIcon,
} from '@heroicons/react/24/solid';

import { selectCurrentUser, logout, selectUserPermissions, selectUserRoles } from '../store/slices/authSlice';
import { authService } from '../services/authService';
import { clinicSettingsService } from '../services/clinicSettings';
import { NotificationBadge } from './components/NotificationBadge';
import { NavLink } from 'react-router-dom';
import { SidebarThemeSelector, THEMES } from '../components/SidebarThemeSelector';

// Memoized navigation items to prevent unnecessary re-renders
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
  { name: 'Dashboard Antrian', href: '/dashboard/queue', icon: QueueListIcon },
  { 
    name: 'Kasir', 
    href: '/cashier', 
    icon: BanknotesIcon 
  },
  { 
    name: 'Photo Data', 
    href: '/photo-data', 
    icon: CameraIcon 
  },
  { 
    name: 'Pasien', 
    icon: UserGroupIcon,
    children: [
      { name: 'Pendaftaran', href: '/patients/register' },
      { name: 'Daftar Pasien', href: '/patients' },
      { name: 'Cari Pasien', href: '/patients/search' },
      { name: 'Impor Pasien (Excel)', href: '/patients/import' },
    ]
  },
  { 
    name: 'Perawat', 
    icon: HeartIcon,
    children: [
      { name: 'Dashboard Perawat', href: '/perawat/dashboard' },
      { name: 'Asesmen Keperawatan', href: '/perawat/assessment' },
      { name: 'Beban Kerja', href: '/perawat/workload' },
      { name: 'Catatan Perawat', href: '/perawat/notes' },
    ]
  },
  { 
    name: 'Dokter', 
    icon: ClipboardDocumentListIcon,
    children: [
      { name: 'Dashboard Dokter', href: '/dokter/dashboard' },
      { name: 'Antrian Pasien', href: '/dokter/queue' },
      { name: 'Pemeriksaan Pasien', href: '/dokter/examination' },
      { name: 'Resep Obat', href: '/dokter/prescriptions' },
    ]
  },
  { 
    name: 'Kebidanan', 
    icon: HeartIcon,
    children: [
      { name: 'Dashboard Kebidanan', href: '/kebidanan/dashboard' },
      { name: 'Antrian Pasien', href: '/kebidanan/queue' },
      { name: 'Pemeriksaan Kebidanan', href: '/kebidanan/examination' },
    ]
  },
  { 
    name: 'Poli Gigi', 
    icon: UserIcon,
    children: [
      { name: 'Antrian Pasien', href: '/dental/queue' },
      { name: 'Pemeriksaan Gigi', href: '/dental/examination' },
    ]
  },

  { 
    name: 'Laboratorium', 
    icon: BeakerIcon,
    children: [
      { name: 'Dashboard Lab', href: '/laboratory' },
      { name: 'Antrian Pasien', href: '/laboratory/queue' },
      { name: 'Permintaan Lab', href: '/laboratory/requests' },
      { name: 'Hasil Lab', href: '/laboratory/results' },
    ]
  },
  { 
    name: 'Radiologi', 
    icon: CameraIcon,
    children: [
      { name: 'Dashboard Radiologi', href: '/radiology' },
      { name: 'Antrian Pasien', href: '/radiology/queue' },
      { name: 'Permintaan Radiologi', href: '/radiology/requests' },
      { name: 'Hasil Radiologi', href: '/radiology/results' },
    ]
  },
  { name: 'Pemeriksaan Penunjang', href: '/supporting-examinations', icon: HeartIcon },
  { 
    name: 'Apotek', 
    icon: BuildingStorefrontIcon,
    children: [
      { name: 'Dashboard Apotek', href: '/pharmacy' },
      { name: 'Antrean Resep (Proses Obat)', href: '/pharmacy/queue' },
      { name: 'Antrian Pasien', href: '/pharmacy/patient-queue' },
      { name: 'Obat-obatan', href: '/pharmacy/medications' },
      { name: 'Penjualan Langsung', href: '/pharmacy/direct-sales' },
      { name: 'Pesanan Stok (PO)', href: '/pharmacy/orders' },
    ]
  },
  { 
    name: 'Manajemen', 
    icon: WrenchScrewdriverIcon,
    children: [
      { name: 'Harga Layanan', href: '/management/pricing' },
      { name: 'Biaya Admin', href: '/management/admin-fee' },
    ]
  },
  {
    name: 'Master Data',
    icon: ClipboardDocumentListIcon,
    children: [
      { name: 'Master Rujukan', href: '/master/referrals' },
      { name: 'Master Travel', href: '/master/travel' },
      { name: 'Master Perusahaan', href: '/master/companies' },
      { name: 'Master Promo', href: '/master/promotions' },
    ]
  },
  { 
    name: 'Keuangan', 
    icon: CurrencyDollarIcon,
    children: [
      { name: 'Dashboard Keuangan', href: '/finance' },
      { name: 'Rekap Cashback & Fee', href: '/finance/cashback' },
      { name: 'Laporan Pajak', href: '/finance/taxes' },
    ]
  },
  { 
    name: 'Marketing', 
    icon: PresentationChartBarIcon,
    children: [
      { name: 'Dashboard', href: '/marketing' },
      { name: 'Dashboard Analisa', href: '/marketing/analisa' },
      { name: 'Manager Marketing', href: '/marketing/manager' },
      { name: 'Manajemen Target', href: '/marketing/targets' },
      { name: 'Rencana Mingguan', href: '/marketing/weekly-plans' },
      { name: 'Laporan Marketing', href: '/marketing/monthly-plans' },
      { name: 'Rekap Cashback & Fee', href: '/marketing/cashback' },
      { name: 'Manajemen Pengguna', href: '/marketing/users' },
    ]
  },
  { 
    name: 'Inventori', 
    icon: CubeIcon,
    children: [
      { name: 'Dashboard', href: '/inventory' },
      { name: 'Manajemen Barang', href: '/inventory/items' },
      { name: 'Pemasok', href: '/inventory/suppliers' },
      { name: 'Kategori', href: '/inventory/categories' },
      { name: 'Pesanan Pembelian', href: '/inventory/purchase-orders' },
      { name: 'Persetujuan Order', href: '/inventory/order-approval' },
    ]
  },
  { 
    name: 'HRD', 
    icon: UserGroupIcon,
    children: [
      { name: 'Dashboard HRD', href: '/hrd' },
      { name: 'Pengaturan Jasa Medis', href: '/hrd/medical-fee' },
    ]
  },
  { 
    name: 'Akreditasi', 
    icon: ShieldCheckIcon,
    children: [
      { name: 'Dashboard', href: '/accreditation' },
      { name: 'Dokumen', href: '/accreditation/documents' },
      { name: 'Asesmen', href: '/accreditation/assessments' },
    ]
  },
  { 
    name: 'Tampilan Antrian', 
    icon: QueueListIcon,
    children: [
      { name: 'Display Dokter', href: '/queue-display/doctor' },
      { name: 'Display Lab', href: '/queue-display/lab' },
      { name: 'Display Apotek', href: '/queue-display/pharmacy' },
      { name: 'Display Radiologi', href: '/queue-display/radiology' },
      { name: 'Display Bidan', href: '/queue-display/midwife' },
      { name: 'Display Gigi', href: '/queue-display/dental' },
    ]
  },
  { 
    name: 'Portal Karyawan', 
    icon: UserCircleIcon,
    children: [
      { name: 'Absensi Karyawan', href: '/absen' },
      { name: 'Pengajuan Cuti', href: '/employee/leave' },
      { name: 'Rekap Absensi', href: '/employee/attendance-recap' },
      { name: 'Slip Gaji', href: '/employee/payslip' },
      { name: 'Order Barang', href: '/employee/order-barang' },
      { name: 'Dashboard Project', href: '/project/dashboard' },
      { name: 'Papan Kanban', href: '/project/board' },
    ]
  },
  { 
    name: 'Manajemen Pengguna', 
    icon: UsersIcon,
    children: [
      { name: 'Pengguna', href: '/user-management/users' },
      { name: 'Pengaturan Klinik', href: '/user-management/clinic-settings' },
    ]
  },
  { 
    name: 'Administrasi', 
    icon: CogIcon,
    children: [
      { name: 'Sinkronisasi SATUSEHAT (Bulk IHS)', href: '/administrasi/satusehat' },
      { name: 'Pelaporan Prolanis', href: '/administrasi/prolanis' },
      { name: 'Pengaturan KBK', href: '/administrasi/kbk' },
      { name: 'Log Aktivitas', href: '/administrasi/activity-log' },
    ]
  },
  { 
    name: 'Laporan', 
    icon: ChartBarIcon,
    children: [
      { name: 'Kepesertaan', href: '/reports/kepesertaan' },
      { name: 'Laporan Bulanan (LB1)', href: '/reports/lb1' },
      { name: 'Prolanis', href: '/reports/prolanis' },
      { name: 'Penyakit Menular', href: '/reports/penyakit-menular' },
      { name: 'Ibu Hamil', href: '/reports/ibu-hamil' },
      { name: 'Peserta KB', href: '/reports/peserta-kb' },
      { name: 'Peserta Prolanis PRB', href: '/reports/peserta-prolanis-prb' },
      { name: 'Rekam Medis Pasien', href: '/reports/medical-records' },
      { name: 'Laporan ICV', href: '/reports/icv' },
      { name: 'Bulk Up. IHS (SATUSEHAT)', href: '/reports/satusehat-bulk' },
    ]
  },
];

// Memoized NavItem component
const NavItem = React.memo(({ item, isExpanded, onToggle, onNavigate, theme }: { item: any; isExpanded: boolean; onToggle: (name: string) => void; onNavigate: () => void; theme: any }) => {
  if (item.children) {
    return (
      <div key={item.name}>
        <button
          onClick={() => onToggle(item.name)}
          className={`w-full flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${theme.id === 'white' ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' : theme.text} group`}
        >
          <div className="flex items-center">
            <item.icon className={`mr-3 h-6 w-6 transition-colors duration-150 ${theme.id === 'white' ? 'text-gray-400 group-hover:text-gray-500' : 'text-white/70 group-hover:text-white'}`} />
            {item.name}
          </div>
          <svg
            className={`ml-2 h-5 w-5 transform transition-transform duration-200 ${
              isExpanded ? 'rotate-90' : ''
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {isExpanded && (
          <div className="ml-8 mt-1 space-y-1 transition-all duration-200">
            {item.children.map((child: any) => (
              <NavLink
                key={child.name}
                to={child.href}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                    isActive
                      ? `${theme.activeText} ${theme.activeBg}`
                      : theme.id === 'white' ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' : theme.text
                  }`
                }
              >
                {child.name}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      key={item.name}
      to={item.href}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
          isActive
            ? `${theme.activeText} ${theme.activeBg}`
            : theme.id === 'white' ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' : theme.text
        } group`
      }
    >
      <item.icon className={`mr-3 h-6 w-6 transition-colors duration-150 ${theme.id === 'white' ? 'text-gray-400 group-hover:text-gray-500' : 'text-white/70 group-hover:text-white'}`} />
      {item.name}
    </NavLink>
  );
});

// Memoized NavItem component
NavItem.displayName = 'NavItem';

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [clinicSettings, setClinicSettings] = useState<any>(null);
  
  const [themeId, setThemeId] = useState(localStorage.getItem('sidebar-theme') || 'white');
  const theme = THEMES.find(t => t.id === themeId) || THEMES[0]!;

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const user = useSelector(selectCurrentUser);
  const permissions = useSelector(selectUserPermissions) || [];
  const roles = useSelector(selectUserRoles) || [];
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('sidebar-theme', themeId);
  }, [themeId]);

  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const settings = await clinicSettingsService.getSettings();
        setClinicSettings(settings);
      } catch (error) {
        console.error('Error fetching clinic settings:', error);
      }
    };

    fetchClinicSettings();
  }, []);

  const handleLogout = useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Force logout and full page reload to clear state
      dispatch(logout());
      window.location.href = '/auth/login';
    }
  }, [dispatch]);

  // Close the mobile drawer after picking a menu item (no-op on desktop,
  // where the sidebar is a separate always-visible block)
  const closeMobileSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const toggleMenuExpansion = useCallback((menuName: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    );
  }, []);

  const getImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const serverUrl = baseUrl.replace('/api', '');
    return `${serverUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const renderClinicBrand = useCallback(() => {
    return (
      <div className="flex flex-col items-center justify-center flex-shrink-0 px-4 w-full">
        {clinicSettings?.clinic_logo ? (
          <div className="w-full flex justify-center mb-3">
            <img 
              src={getImageUrl(clinicSettings.clinic_logo)} 
              alt="Clinic Logo" 
              className="h-16 w-auto max-w-[200px] object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        ) : (
           <div className="overflow-hidden flex items-center justify-center mb-3" style={{ width: '120px', height: '48px' }}>
             <img src="/Logo macLi.png" alt="macLi Logo" className="w-full h-full object-contain transform scale-[1.8]" />
           </div>
        )}
        {clinicSettings?.clinic_name && (
          <span className={`text-lg font-bold text-center w-full px-1 truncate ${theme?.id === 'white' ? 'text-gray-900' : 'text-white'}`}>
            {clinicSettings.clinic_name}
          </span>
        )}
      </div>
    );
  }, [clinicSettings, theme]);

  const hasAccess = useCallback((item: any) => {
    if (roles.includes('superadmin') || roles.includes('admin')) return true;

    const checkPerm = (prefix: string) => permissions.some(p => p.startsWith(prefix));

    switch (item.name) {
      case 'Dashboard': return checkPerm('dashboard');
      case 'Dashboard Antrian': return checkPerm('dashboard_antrian');
      case 'Kasir': return checkPerm('kasir');
      case 'Photo Data': return checkPerm('photo_data');
      case 'Pasien': return checkPerm('pasien') || checkPerm('pendaftaran');
      case 'Perawat': return checkPerm('perawat');
      case 'Dokter': return checkPerm('dokter');
      case 'Kebidanan': return checkPerm('kebidanan');
      case 'Poli Gigi': return checkPerm('poligigi');
      case 'Laboratorium': return checkPerm('laboratorium');
      case 'Radiologi': return checkPerm('radiologi');
      case 'Pemeriksaan Penunjang': return checkPerm('pemeriksaan_penunjang');
      case 'Apotek': return checkPerm('apotek');
      case 'Manajemen': return checkPerm('manajemen');
      case 'Master Data': return checkPerm('master_data');
      case 'Keuangan': return checkPerm('keuangan');
      case 'Marketing': return checkPerm('marketing');
      case 'Inventori': return checkPerm('inventori');
      case 'HRD': return checkPerm('hrd');
      case 'Akreditasi': return checkPerm('akreditasi');
      case 'Tampilan Antrian': return checkPerm('tampilan_antrian');
      case 'Portal Karyawan': return checkPerm('portal_karyawan');
      case 'Manajemen Pengguna': return checkPerm('manajemen_pengguna');
      case 'Administrasi': return checkPerm('administrasi');
      case 'Laporan': return checkPerm('laporan');
      default:
        return true;
    }
  }, [permissions, roles]);

  // Memoize navigation items rendering
  const renderNavItems = useMemo(() => {
    return navigation
      .filter(hasAccess)
      .map(item => {
        // If user is admin/superadmin, show all children
        if (roles.includes('superadmin') || roles.includes('admin') || !item.children) {
          return (
            <NavItem
              key={item.name}
              item={item}
              isExpanded={expandedMenus.includes(item.name)}
              onToggle={toggleMenuExpansion}
              onNavigate={closeMobileSidebar}
              theme={theme}
            />
          );
        }

        // Filter children based on specific permissions
        const filteredChildren = item.children.filter(child => {
          
          const permMapping: Record<string, string> = {
            'Pendaftaran': 'pasien.pendaftaran',
            'Daftar Pasien': 'pasien.daftar',
            'Cari Pasien': 'pasien.cari',
            'Impor Pasien (Excel)': 'pasien.impor',
            'Dashboard Perawat': 'perawat.dashboard',
            'Asesmen Keperawatan': 'perawat.assessment',
            'Beban Kerja': 'perawat.workload',
            'Catatan Perawat': 'perawat.notes',
            'Dashboard Dokter': 'dokter.dashboard',
            'Antrian Pasien': child.href.includes('dokter') ? 'dokter.queue' 
              : child.href.includes('kebidanan') ? 'kebidanan.queue'
              : child.href.includes('dental') ? 'poligigi.queue'
              : child.href.includes('laboratory') ? 'laboratorium.queue'
              : child.href.includes('radiology') ? 'radiology.queue'
              : child.href.includes('pharmacy') ? 'apotek.patient-queue'
              : '',
            'Pemeriksaan Pasien': 'dokter.examination',
            'Resep Obat': 'dokter.prescriptions',
            'Dashboard Kebidanan': 'kebidanan.dashboard',
            'Pemeriksaan Kebidanan': 'kebidanan.examination',
            'Pemeriksaan Gigi': 'poligigi.examination',
            'Dashboard Lab': 'laboratorium.dashboard',
            'Permintaan Lab': 'laboratorium.requests',
            'Hasil Lab': 'laboratorium.results',
            'Dashboard Radiologi': 'radiologi.dashboard',
            'Permintaan Radiologi': 'radiologi.requests',
            'Hasil Radiologi': 'radiologi.results',
            'Dashboard Apotek': 'apotek.dashboard',
            'Obat-obatan': 'apotek.medications',
            'Pesanan': 'apotek.orders',
            'Harga Layanan': 'manajemen.pricing',
            'Biaya Admin': 'manajemen.admin-fee',
            'Master Rujukan': 'master_data.referrals',
            'Master Travel': 'master_data.travel',
            'Master Perusahaan': 'master_data.companies',
            'Master Promo': 'master_data.promotions',
            'Dashboard Keuangan': 'keuangan.dashboard',
            'Laporan Pajak': 'keuangan.taxes',
            'Dashboard': item.name === 'Marketing' ? 'marketing.dashboard' 
              : item.name === 'Inventori' ? 'inventori.dashboard' 
              : item.name === 'Akreditasi' ? 'akreditasi.dashboard' : '',
            'Dashboard Analisa': 'marketing.analisa',
            'Manager Marketing': 'marketing.manager',
            'Manajemen Target': 'marketing.targets',
            'Rencana Mingguan': 'marketing.weekly-plans',
            'Laporan Marketing': 'marketing.monthly-plans',
            'Rekap Cashback & Fee': child.href.includes('finance') ? 'keuangan.cashback' : 'marketing.cashback',
            'Manajemen Pengguna': item.name === 'Marketing' ? 'marketing.users' : '',
            'Manajemen Barang': 'inventori.items',
            'Pemasok': 'inventori.suppliers',
            'Pesanan Pembelian': 'inventori.purchase-orders',
            'Persetujuan Order': 'inventori.order-approval',
            'Dashboard HRD': 'hrd.dashboard',
            'Pengaturan Jasa Medis': 'hrd.medical-fee',
            'Dokumen': 'akreditasi.documents',
            'Asesmen': 'akreditasi.assessments',
            'Display Dokter': 'tampilan_antrian.doctor',
            'Display Lab': 'tampilan_antrian.lab',
            'Display Apotek': 'tampilan_antrian.pharmacy',
            'Display Radiologi': 'tampilan_antrian.radiology',
            'Display Bidan': 'tampilan_antrian.midwife',
            'Display Gigi': 'tampilan_antrian.dental',
            'Absensi Karyawan': 'portal_karyawan.absen',
            'Pengajuan Cuti': 'portal_karyawan.leave',
            'Rekap Absensi': 'portal_karyawan.attendance-recap',
            'Dashboard Project': 'portal_karyawan.project',
            'Papan Kanban': 'portal_karyawan.board',
            'Slip Gaji': 'portal_karyawan.payslip',
            'Order Barang': 'portal_karyawan.order-barang',
            'Pengguna': 'manajemen_pengguna.users',
            'Pengaturan Klinik': 'manajemen_pengguna.clinic-settings',
            'Pengaturan SATUSEHAT': 'administrasi.satusehat',
            'Sinkronisasi SATUSEHAT (Bulk IHS)': 'administrasi.satusehat',
            'Pelaporan Prolanis': 'administrasi.prolanis',
            'Pengaturan KBK': 'administrasi.kbk',
            'Log Aktivitas': 'administrasi.activity-log',
            'Kepesertaan': 'laporan.kepesertaan',
            'Laporan Bulanan (LB1)': 'laporan.lb1',
            'Prolanis': 'laporan.prolanis',
            'Penyakit Menular': 'laporan.penyakit-menular',
            'Ibu Hamil': 'laporan.ibu-hamil',
            'Peserta KB': 'laporan.peserta-kb',
            'Peserta Prolanis PRB': 'laporan.peserta-prolanis-prb',
            'Rekam Medis Pasien': 'laporan.medical-records',
            'Laporan ICV': 'laporan.icv',
            'Bulk Up. IHS (SATUSEHAT)': 'laporan.satusehat'
          };
          
          const permKey = permMapping[child.name];
          if (!permKey) return true; // Default allow if not mapped strictly
          return permissions.includes(permKey);
        });

        // Hide parent if no children are allowed and it's a dropdown menu
        if (filteredChildren.length === 0 && item.children.length > 0) {
          return null;
        }

        return (
          <NavItem
            key={item.name}
            item={{ ...item, children: filteredChildren }}
            isExpanded={expandedMenus.includes(item.name)}
            onToggle={toggleMenuExpansion}
            onNavigate={closeMobileSidebar}
            theme={theme}
          />
        );
      });
  }, [expandedMenus, toggleMenuExpansion, closeMobileSidebar, hasAccess, permissions, roles, theme]);

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Sidebar for mobile */}
      <div className={`fixed inset-0 flex z-40 md:hidden print:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className={`relative flex-1 flex flex-col max-w-xs w-full rounded-r-[2rem] shadow-xl transition-colors duration-300 ${theme?.bg || 'bg-white'}`}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            {renderClinicBrand()}
            <nav className="mt-5 px-2 space-y-1">
              {renderNavItems}
            </nav>
          </div>
          <SidebarThemeSelector current={themeId} onChange={setThemeId} />
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className={`hidden md:flex md:flex-shrink-0 print:hidden transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'w-0 -ml-64 overflow-hidden opacity-0 pointer-events-none' : 'w-64 ml-0 opacity-100'}`}>
        <div className="flex flex-col w-64">
          <div className={`flex flex-col h-0 flex-1 border-r border-gray-200/20 rounded-r-[2rem] shadow-lg transition-colors duration-300 ${theme?.bg || 'bg-white'}`}>
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              {renderClinicBrand()}
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {renderNavItems}
              </nav>
            </div>
            <SidebarThemeSelector current={themeId} onChange={setThemeId} />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow print:hidden">
          {/* Mobile menu toggle */}
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Desktop sidebar collapse toggle */}
          <button
            type="button"
            className="hidden md:flex items-center justify-center px-4 border-r border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors"
            onClick={() => setIsSidebarCollapsed(prev => !prev)}
            title={isSidebarCollapsed ? "Tampilkan Sidebar" : "Minimize / Sembunyikan Sidebar"}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              {/* Search can be added here */}
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              {/* Notifications */}
              <NotificationBadge roles={roles} permissions={permissions} />

              {/* Profile dropdown */}
              <div className="ml-3 relative">
                <button
                  className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  <span className="ml-2 text-sm font-medium text-gray-700 hidden md:block">
                    {user?.name}
                  </span>
                </button>
                
                {userMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 transition-all duration-200">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-700 border-b">
                        <div className="font-medium">{user?.name}</div>
                        <div className="text-gray-500">{user?.email}</div>
                        <div className="text-xs text-primary-600 capitalize">{user?.role}</div>
                      </div>
                      <NavLink to="/user-management/clinic-settings" className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150">
                        <Cog6ToothIcon className="mr-3 h-4 w-4" />
                        Settings
                      </NavLink>
                      <button 
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
              <div key={location.pathname} className="page-transition">
                <Outlet />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;